import React, { useEffect, useState } from 'react';
import { useTalentos } from '../../../../application/talentos/hooks/useTalentos';
import ToolbarComponent from '../../../ui/section';
import Loader from '../../../ui/loader';
import TalentosAPI from '../../../../api/talentos';
import './detail.css';

const EmployerDetailPage = (props) => {
    const { getUserById, isLoading, error } = useTalentos();
    const [employer, setEmployer] = useState(null);
    const [company, setCompany] = useState(null);
    const [loadingCompany, setLoadingCompany] = useState(false);

    useEffect(() => {
        const id = props.match?.params?.id;
        if (id) {
            loadEmployer(id);
        }
    }, [props.match?.params?.id]);

    const loadEmployer = async (id) => {
        try {
            const data = await getUserById(id);
            setEmployer(data);
            
            // Si hay una compañía asociada, cargar sus datos completos
            if (data.company) {
                await loadCompany(data.company);
            }
        } catch (err) {
            console.error('Error al cargar empleador:', err);
        }
    };

    const loadCompany = async (companyData) => {
        setLoadingCompany(true);
        try {
            // Si company es un string (ID), obtener los datos completos
            if (typeof companyData === 'string') {
                const companyDataFull = await TalentosAPI.getCompanyById(companyData);
                setCompany(companyDataFull);
            } 
            // Si company es un objeto pero solo tiene ID, obtener datos completos
            else if (companyData._id && !companyData.name) {
                const companyDataFull = await TalentosAPI.getCompanyById(companyData._id);
                setCompany(companyDataFull);
            }
            // Si company ya tiene datos completos, usarlos directamente
            else {
                setCompany(companyData);
            }
        } catch (err) {
            console.error('Error al cargar compañía:', err);
            // Si falla, usar los datos parciales que vienen con el empleador
            setCompany(companyData);
        } finally {
            setLoadingCompany(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (isLoading || loadingCompany) {
        return (
            <ToolbarComponent label="Detalle del Empleador" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <Loader text="Cargando información..." />
            </ToolbarComponent>
        );
    }

    if (error || !employer) {
        return (
            <ToolbarComponent label="Detalle del Empleador" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <div className="error-message">
                    {error || 'No se pudo cargar el empleador'}
                </div>
            </ToolbarComponent>
        );
    }

    return (
        <ToolbarComponent label="Detalle del Empleador" showBackButton={true} onBackClick={() => props.history.goBack()}>
            <div className="employer-detail-container">
                {/* Header */}
                <div className="detail-header">
                    <div>
                        <h1 className="detail-title">
                            {employer.name || ''} {employer.lastname || ''}
                        </h1>
                        {company && (
                            <p className="detail-company">
                                {company.name || (typeof employer.company === 'object' ? employer.company.name : employer.company) || '-'}
                            </p>
                        )}
                    </div>
                </div>

                {/* Información Personal */}
                <div className="detail-section">
                    <h2 className="section-title">Información Personal</h2>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Nombre</span>
                            <span className="info-value">{employer.name || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Apellido</span>
                            <span className="info-value">{employer.lastname || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Email</span>
                            <span className="info-value">{employer.email || '-'}</span>
                        </div>
                        {employer.phone && (
                            <div className="info-item">
                                <span className="info-label">Teléfono</span>
                                <span className="info-value">{employer.phone}</span>
                            </div>
                        )}
                        {employer.country && (
                            <div className="info-item">
                                <span className="info-label">País</span>
                                <span className="info-value">{employer.country}</span>
                            </div>
                        )}
                        {employer.city && (
                            <div className="info-item">
                                <span className="info-label">Ciudad</span>
                                <span className="info-value">{employer.city}</span>
                            </div>
                        )}
                        {employer.createdAt && (
                            <div className="info-item">
                                <span className="info-label">Fecha de registro</span>
                                <span className="info-value">{formatDate(employer.createdAt)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Información de la Compañía */}
                {company && (
                    <div className="detail-section">
                        <h2 className="section-title">Información de la Compañía</h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Nombre</span>
                                <span className="info-value">{company.name || '-'}</span>
                            </div>
                            {company.description && (
                                <div className="info-item full-width">
                                    <span className="info-label">Descripción</span>
                                    <span className="info-value">{company.description}</span>
                                </div>
                            )}
                            {company.website && (
                                <div className="info-item">
                                    <span className="info-label">Sitio web</span>
                                    <span className="info-value">
                                        <a href={company.website} target="_blank" rel="noopener noreferrer">
                                            {company.website}
                                        </a>
                                    </span>
                                </div>
                            )}
                            {company.country && (
                                <div className="info-item">
                                    <span className="info-label">País</span>
                                    <span className="info-value">{company.country}</span>
                                </div>
                            )}
                            {company.city && (
                                <div className="info-item">
                                    <span className="info-label">Ciudad</span>
                                    <span className="info-value">{company.city}</span>
                                </div>
                            )}
                            {company.region && (
                                <div className="info-item">
                                    <span className="info-label">Región</span>
                                    <span className="info-value">{company.region}</span>
                                </div>
                            )}
                            {company.logo && (
                                <div className="info-item full-width">
                                    <span className="info-label">Logo</span>
                                    <span className="info-value">
                                        <img src={company.logo} alt={company.name} style={{ maxWidth: '200px', maxHeight: '100px' }} />
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Información Adicional */}
                {(employer.bio || employer.linkedin || employer.twitter) && (
                    <div className="detail-section">
                        <h2 className="section-title">Información Adicional</h2>
                        <div className="info-grid">
                            {employer.bio && (
                                <div className="info-item full-width">
                                    <span className="info-label">Biografía</span>
                                    <span className="info-value">{employer.bio}</span>
                                </div>
                            )}
                            {employer.linkedin && (
                                <div className="info-item">
                                    <span className="info-label">LinkedIn</span>
                                    <span className="info-value">
                                        <a href={employer.linkedin} target="_blank" rel="noopener noreferrer">
                                            {employer.linkedin}
                                        </a>
                                    </span>
                                </div>
                            )}
                            {employer.twitter && (
                                <div className="info-item">
                                    <span className="info-label">Twitter</span>
                                    <span className="info-value">
                                        <a href={employer.twitter} target="_blank" rel="noopener noreferrer">
                                            {employer.twitter}
                                        </a>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ToolbarComponent>
    );
};

export default EmployerDetailPage;

